import { useAtom } from 'jotai';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
	createLog,
	DMSProvider,
	INVENTORY_SECTIONS,
	LogSeverity,
	Services,
	type ServicesProvider,
} from '@ekhodealer/ekho-common';
import {
	Box,
	Form,
} from '@ekhodealer/ekho-common/rev';

import type {
	DMSConnectionConfig,
	LightspeedDmsConnectionConfig,	OtherDmsConnectionConfig} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useCreateInventoryDmsConnection } from '../../api/configuration/inventory/useCreateInventoryDmsConnection';
import { forceValidateDmsConnectionAtom } from '../../atoms/onboardingValidationAtoms';
import { useServiceProgramInfo } from '../../hooks/useServiceProgramInfo';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(servicesProvider: ServicesProvider | undefined | null): Partial<{
	dmsProvider: DMSProvider;
	cmf: string;
	otherDmsName: string;
	hasProvidedAuthConsent: boolean;
}> {
	const dmsConnection = servicesProvider?.config?.[Services.INVENTORY]?.dmsConnectionConfig;
	if (!dmsConnection) {
		return {
			dmsProvider: undefined,
			cmf: '',
			otherDmsName: '',
			hasProvidedAuthConsent: false,
		};
	}

	const { dmsProvider, connectionDetails, hasProvidedAuthConsent } = dmsConnection;
	switch (dmsProvider) {
		case DMSProvider.OTHER:
			return {
				dmsProvider,
				cmf: '',
				otherDmsName: (connectionDetails as OtherDmsConnectionConfig).name || '',
				hasProvidedAuthConsent: hasProvidedAuthConsent || false,
			};
		case DMSProvider.LIGHTSPEED:
			return {
				dmsProvider,
				cmf: (connectionDetails as LightspeedDmsConnectionConfig).cmf || '',
				otherDmsName: '',
				hasProvidedAuthConsent: hasProvidedAuthConsent || false,
			};
		default:
			return {
				dmsProvider: undefined,
				cmf: '',
				otherDmsName: '',
				hasProvidedAuthConsent: false,
			};
	}
}

/**
 * Helper function to get DMS connection config from form data
 */
function getConfigFromFormData(data: {
	dmsProvider: DMSProvider;
	cmf?: string;
	otherDmsName?: string;
	hasProvidedAuthConsent: boolean;
}): DMSConnectionConfig {
	switch (data.dmsProvider) {
		case DMSProvider.OTHER:
			if (!data.otherDmsName?.trim()) {
				throw new Error('Other DMS name is required');
			}
			return {
				dmsProvider: data.dmsProvider,
				connectionDetails: { name: data.otherDmsName.trim() },
				hasProvidedAuthConsent: data.hasProvidedAuthConsent,
			};
		case DMSProvider.LIGHTSPEED:
			if (!data.cmf?.trim()) {
				throw new Error('CMF number is required');
			}
			return {
				dmsProvider: data.dmsProvider,
				connectionDetails: { cmf: data.cmf.trim() },
				hasProvidedAuthConsent: data.hasProvidedAuthConsent,
			};
		default:
			throw new Error('Invalid DMS provider');
	}
}

const dmsConnectionSchema = z
	.object({
		dmsProvider: z.nativeEnum(DMSProvider, {
			required_error: 'Please select a DMS provider',
			invalid_type_error: 'Please select a DMS provider',
		}),
		cmf: z.string().optional(),
		otherDmsName: z.string().optional(),
		hasProvidedAuthConsent: z.boolean().refine((val) => val === true, {
			message: 'You must authorize Ekho to access your DMS data',
		}),
	})
	.refine(
		(data) => {
			if (data.dmsProvider === DMSProvider.LIGHTSPEED) {
				return data.cmf && data.cmf.trim().length > 0;
			}
			return true;
		},
		{
			message: 'CMF number is required for Lightspeed DMS',
			path: ['cmf'],
		},
	)
	.refine(
		(data) => {
			if (data.dmsProvider === DMSProvider.OTHER) {
				return data.otherDmsName && data.otherDmsName.trim().length > 0;
			}
			return true;
		},
		{
			message: 'DMS name is required for Other provider',
			path: ['otherDmsName'],
		},
	);

type DmsConnectionFormData = z.infer<typeof dmsConnectionSchema>;

/**
 * Component for managing Inventory Service DMS Connection Configuration
 */
export function DmsConnectionForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const createInventoryDmsConnectionMutation = useCreateInventoryDmsConnection();
	const programInfo = useServiceProgramInfo(INVENTORY_SECTIONS.DMS);
	const [forceValidation, setForceValidation] = useAtom(forceValidateDmsConnectionAtom);

	// Early return if subsection is not part of the program or is Ekho-only
	if (!servicesProvider || !programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (data: DmsConnectionFormData): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			await createInventoryDmsConnectionMutation.mutateAsync({
				dmsConnectionConfig: getConfigFromFormData(data),
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to create DMS connection: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const providerOptions = [
		{ id: DMSProvider.LIGHTSPEED, label: 'Lightspeed DMS' },
		{ id: DMSProvider.OTHER, label: 'Other' },
	];

	return (
		<Box padding='24' background='primary' rounding='12' elevation='0'>
			<Form
				name='inventory-service-dms-connection'
				schema={dmsConnectionSchema}
				defaultValues={getDefaultValues(servicesProvider)}
				onSubmit={handleSubmit}
				forceValidation={forceValidation}
				title='Select your DMS provider'
				titleMaxWidth='100%'
				showSaveAndClear>
				<DmsConnectionFormFields providerOptions={providerOptions} />
			</Form>
		</Box>
	);
}

/**
 * Component that renders the form fields and watches dmsProvider for conditional fields
 */
function DmsConnectionFormFields({
	providerOptions,
}: {
	providerOptions: Array<{ id: DMSProvider; label: string }>;
}) {
	const dmsProvider = useWatch({ name: 'dmsProvider' });
	const isLightspeed = dmsProvider === DMSProvider.LIGHTSPEED;
	const isOther = dmsProvider === DMSProvider.OTHER;

	return (
		<>
			<Form.Field.Dropdown
				schemaField='dmsProvider'
				labelText='DMS Provider'
				required
				options={providerOptions}
				placeholder='Select DMS provider'
				buttonVariant='outline'
			/>
			{isLightspeed && (
				<Form.Field.Text
					schemaField='cmf'
					labelText='CMF Number'
					descriptionText="You can find your CMF number in Lightspeed's settings"
					required
					size='large'
					placeholder='Enter CMF number'
				/>
			)}
			{isOther && (
				<Form.Field.Text
					schemaField='otherDmsName'
					labelText='DMS Name'
					descriptionText='Enter the name of your DMS provider'
					required
					size='large'
					placeholder='Enter DMS name'
				/>
			)}
			<Form.Field.Checkbox
				schemaField='hasProvidedAuthConsent'
				labelText='I authorize Ekho to access my DMS data'
				descriptionText="See Ekho's Privacy Policy for details."
				required
			/>
		</>
	);
}
